import { monthOf, type Month } from './dates.js';
import { round2 } from './money.js';
import { NO_CATEGORY, type Category, type CategoryKind, type Movement, type Part } from './model.js';

/**
 * Movimiento con sus valores efectivos (los mismos que calculan las columnas grises del libro):
 * - un reembolso (importe > 0) con Grupo hereda la categoría, el mes y el "compartido" del primer
 *   gasto del Grupo que tenga categoría; su categoría propia solo manda si la tiene;
 * - sin categoría → "Sin categoría", que cuenta como gasto o ingreso según el signo;
 * - atribuible = importe × ½ si es compartido; 0 si la categoría no cuenta.
 */
export interface EffectiveMovement extends Movement {
  effCategory: string;
  effMonth: Month;
  effShared: boolean;
  kind: CategoryKind;
  attributable: number;
}

export interface EffectivePart extends Part {
  effMonth: Month | '';
  kind: CategoryKind;
  attributable: number;
}

/** Tipo por categoría, sin distinguir mayúsculas (como XLOOKUP en el libro); gana la primera fila. */
export function categoryKinds(categories: Category[]): Map<string, CategoryKind> {
  const out = new Map<string, CategoryKind>();
  for (const c of categories) if (!out.has(c.name.toLowerCase())) out.set(c.name.toLowerCase(), c.kind);
  return out;
}

function kindOf(kinds: Map<string, CategoryKind>, category: string, amount: number): CategoryKind {
  return kinds.get(category.toLowerCase()) ?? (amount < 0 ? 'expense' : 'income');
}

function attributableOf(kind: CategoryKind, amount: number, shared: boolean): number {
  return kind === 'excluded' ? 0 : round2(amount * (shared ? 0.5 : 1));
}

export function effectiveMovements(movements: Movement[], categories: Category[]): EffectiveMovement[] {
  const kinds = categoryKinds(categories);
  const groupExpense = new Map<string, Movement>();
  for (const m of movements) {
    const k = m.group.toLowerCase();
    if (m.amount < 0 && k && m.category && !groupExpense.has(k)) groupExpense.set(k, m);
  }
  return movements.map(m => {
    const g = m.group ? groupExpense.get(m.group.toLowerCase()) : undefined;
    const reimbursement = m.amount > 0 && g !== undefined;
    const effCategory = m.category || (g ? g.category : NO_CATEGORY);
    const kind = kindOf(kinds, effCategory, m.amount);
    const effShared = reimbursement ? g.shared : m.shared;
    const effMonth = m.month || (reimbursement ? (g.month || monthOf(g.date)) : monthOf(m.date));
    return { ...m, effCategory, effMonth, effShared, kind, attributable: attributableOf(kind, m.amount, effShared) };
  });
}

/** Las partes heredan el mes y el "compartido" del movimiento dividido. */
export function effectiveParts(parts: Part[], movements: EffectiveMovement[], categories: Category[]): EffectivePart[] {
  const kinds = categoryKinds(categories);
  const byId = new Map(movements.map(m => [m.id, m]));
  return parts.map(p => {
    const m = byId.get(p.movementId);
    const kind = kindOf(kinds, p.category, p.amount);
    return { ...p, effMonth: m ? m.effMonth : '', kind, attributable: attributableOf(kind, p.amount, m ? m.effShared : false) };
  });
}
